/**
 * Role 9 — Sub-classification of Assets, Liabilities and Equity — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role9 (210100 - Sub-classification) totals based on form data
 *
 * @param formData - The form data object with control values (format: qname[member][period])
 * @param xbrlPrefix - The XBRL prefix ('ifrs-full' for MFRS, 'ifrs-smes' for MPERS)
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_210100(
  formData: Record<string, any>,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  // Helper function to get control name in XBRL format
  const getXbrlControlName = (
    qname: string,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous',
  ): string => {
    const periodLabel = period === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${qname}[${consolidatedOrSeparate}][${periodLabel}]`;
  };

  // Process all combinations of Consolidated/Separate and Current/Previous
  ['Consolidated', 'Separate'].forEach((consolidatedOrSeparate) => {
    ['Current', 'Previous'].forEach((period) => {
      const getValue = (qname: string) => {
        const controlName = getXbrlControlName(
          qname,
          consolidatedOrSeparate as any,
          period as any,
        );
        const value = formData[controlName];
        // Explicitly convert to number to avoid string concatenation
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      };

      const setValue = (qname: string, value: number) => {
        const controlName = getXbrlControlName(
          qname,
          consolidatedOrSeparate as any,
          period as any,
        );
        calculatedTotals[controlName] = value;
      };

      // Get prefix for qnames
      const prefix = xbrlPrefix;
      const customPrefix =
        prefix === 'ifrs-full' ? 'ssmt-mfrs' : 'ssmt-mpers';
      const specialPrefix = 'ssmt';

      /*
        Property, Plant and Equipment - MFRS and MPERS Calculations
      */
      //#region Property, Plant and Equipment

      // 1. Total Land = Sum of land types
      const totalLand =
        getValue(`${customPrefix}:FreeholdLand`) +
        getValue(`${customPrefix}:LongtermLeaseholdLand`) +
        getValue(`${customPrefix}:ShorttermLeaseholdLand`);
      setValue(`${prefix}:Land`, totalLand);

      // 2. Total Buildings = Sum of building types
      const totalBuildings =
        getValue(`${customPrefix}:BuildingOnFreeholdLand`) +
        getValue(`${customPrefix}:BuildingOnLongtermLeaseholdLand`) +
        getValue(`${customPrefix}:BuildingOnShorttermLeaseholdLand`) +
        getValue(`${customPrefix}:LeasedProperties`);
      setValue(`${prefix}:Buildings`, totalBuildings);

      // 3. Total Land and Buildings = Total Land + Total Buildings
      const totalLandAndBuildings = totalLand + totalBuildings;
      setValue(`${prefix}:LandAndBuildings`, totalLandAndBuildings);

      // MPERS
      if (prefix === 'ifrs-smes') {
        // 4. Total Property, Plant and Equipment = Sum of all PPE items
        const totalPPE =
          totalLandAndBuildings +
          getValue(`${prefix}:Machinery`) +
          getValue(`${prefix}:Vehicles`) +
          getValue(`${customPrefix}:OfficeEquipmentFixtureAndFittings`) +
          getValue(`${customPrefix}:PlantAndEquipment`) +
          getValue(`${prefix}:ConstructionInProgress`) +
          getValue(`${prefix}:OtherPropertyPlantAndEquipment`);
        setValue(`${prefix}:PropertyPlantAndEquipment`, totalPPE);
      }

      // MFRS
      if (prefix === 'ifrs-full') {
        // 4. Total Vehicles = Sum of vehicle types
        const totalVehicles =
          getValue(`${customPrefix}:Vessels`) +
          getValue(`${prefix}:Aircraft`) +
          getValue(`${prefix}:MotorVehicles`) +
          getValue(`${customPrefix}:OtherVehicles`);
        setValue(`${prefix}:Vehicles`, totalVehicles);

        // 5. Total Property, Plant and Equipment = Sum of all PPE items
        const totalPPE =
          totalLandAndBuildings +
          totalVehicles +
          getValue(`${prefix}:Machinery`) +
          getValue(`${customPrefix}:PlantAndEquipment`) +
          getValue(`${customPrefix}:OfficeEquipmentFixtureAndFittings`) +
          getValue(`${customPrefix}:ComputerSoftwareAndHardware`) +
          getValue(`${prefix}:BearerPlants`) +
          getValue(`${customPrefix}:InfrastructureAndSiteFacilities`) +
          getValue(`${prefix}:MiningAssets`) +
          getValue(`${prefix}:OilAndGasAssets`) +
          getValue(`${customPrefix}:TangibleExplorationAndEvaluationAssets`) +
          getValue(`${customPrefix}:TelecommunicationEquipments`) +
          getValue(`${prefix}:ConstructionInProgress`) +
          getValue(`${prefix}:OtherPropertyPlantAndEquipment`);
        setValue(`${prefix}:PropertyPlantAndEquipment`, totalPPE);
      }

      //#endregion Property, Plant and Equipment

      /*
        Investment properties - MFRS and MPERS Calculations
      */
      //#region Investment properties

      // MPERS
      if (prefix === 'ifrs-smes') {
        // 1. Total Investment Properties
        const freeholdLand = getValue(
          `${customPrefix}:InvestmentPropertyFreeholdLandAndBuilding`,
        );
        const longtermLeasehold = getValue(
          `${customPrefix}:InvestmentPropertyLongtermLeaseholdLand`,
        );
        const shorttermLeasehold = getValue(
          `${customPrefix}:InvestmentPropertyShorttermLeaseholdLand`,
        );
        const buildings = getValue(
          `${customPrefix}:InvestmentPropertyBuildingUnderConstruction`,
        );
        const otherInvestment = getValue(
          `${customPrefix}:OtherInvestmentProperty`,
        );

        const totalInvestmentProperty =
          freeholdLand +
          longtermLeasehold +
          shorttermLeasehold +
          buildings +
          otherInvestment;

        setValue(`${prefix}:InvestmentProperty`, totalInvestmentProperty);
      }

      // MFRS
      if (prefix === 'ifrs-full') {
        // 1. Total Investment Properties Completed = Freehold + Leasehold
        const totalInvestmentPropertyCompleted =
          getValue(
            `${customPrefix}:InvestmentPropertyFreeholdLandAndBuilding`,
          ) +
          getValue(
            `${customPrefix}:InvestmentPropertyLeaseholdLandAndBuilding`,
          );
        setValue(
          `${prefix}:InvestmentPropertyCompleted`,
          totalInvestmentPropertyCompleted,
        );

        // 2. Total Investment Properties Under Construction = Building Under Construction
        const totalInvestmentPropertyUnderConstruction = getValue(
          `${customPrefix}:InvestmentPropertyBuildingUnderConstruction`,
        );
        setValue(
          `${prefix}:InvestmentPropertyUnderConstructionOrDevelopment`,
          totalInvestmentPropertyUnderConstruction,
        );

        // 3. Total Investment Properties = Completed + Under Construction + Other
        const totalInvestmentPropertyMFRS =
          totalInvestmentPropertyCompleted +
          totalInvestmentPropertyUnderConstruction +
          getValue(`${customPrefix}:OtherInvestmentProperty`);
        setValue(`${prefix}:InvestmentProperty`, totalInvestmentPropertyMFRS);
      }

      //#endregion Investment properties

      /*
        Biological Assets - MFRS Calculations
      */
      // #region Biological Assets
      if (prefix === 'ifrs-full') {
        const totalBiologicalAssets =
          getValue(`${customPrefix}:ConsumableBiologicalAssets`) +
          getValue(`${customPrefix}:BearerBiologicalAssets`);
        setValue(`${prefix}:BiologicalAssets`, totalBiologicalAssets);
      }
      // #endregion Biological Assets

      /*
        Intangible assets and goodwill - MFRS and MPERS Calculations
      */
      // #region Intangible assets and goodwill

      // MPERS
      if (prefix === 'ifrs-smes') {
        // 1. Total Intangible Assets Other Than Goodwill = Copyrights + Other
        const totalIntangiblesOtherThanGoodwill =
          getValue(
            `${prefix}:CopyrightsPatentsAndOtherIndustrialPropertyRightsServiceAndOperatingRights`,
          ) + getValue(`${prefix}:OtherIntangibleAssets`);
        setValue(
          `${prefix}:IntangibleAssetsOtherThanGoodwill`,
          totalIntangiblesOtherThanGoodwill,
        );

        // 2. Total Intangible Assets and Goodwill = Total Other Than Goodwill + Goodwill
        const totalIntangibleAssetsAndGoodwill =
          totalIntangiblesOtherThanGoodwill + getValue(`${prefix}:Goodwill`);
        setValue(
          `${prefix}:IntangibleAssetsAndGoodwill`,
          totalIntangibleAssetsAndGoodwill,
        );
      }

      // MFRS
      if (prefix === 'ifrs-full') {
        // 1. Total Intangible Assets Other Than Goodwill = Sum of all intangible assets except goodwill
        const totalIntangiblesOtherThanGoodwill =
          getValue(
            `${prefix}:CustomerrelatedIntangibleAssetsRecognisedAsOfAcquisitionDate`,
          ) +
          getValue(`${prefix}:BrandNames`) +
          getValue(`${customPrefix}:CustomerRelationships`) +
          getValue(
            `${prefix}:CopyrightsPatentsAndOtherIndustrialPropertyRightsServiceAndOperatingRights`,
          ) +
          getValue(`${prefix}:IntangibleAssetsUnderDevelopment`) +
          getValue(`${prefix}:IntangibleExplorationAndEvaluationAssets`) +
          getValue(`${prefix}:LicencesAndFranchises`) +
          getValue(`${prefix}:RecipesFormulaeModelsDesignsAndPrototypes`) +
          getValue(`${prefix}:ComputerSoftware`) +
          getValue(`${customPrefix}:SmallHolderRelationship`) +
          getValue(`${prefix}:OtherIntangibleAssets`);
        setValue(
          `${prefix}:IntangibleAssetsOtherThanGoodwill`,
          totalIntangiblesOtherThanGoodwill,
        );

        // 2. Total Intangible Assets and Goodwill = Total Other Than Goodwill + Goodwill
        const totalIntangibleAssetsAndGoodwill =
          totalIntangiblesOtherThanGoodwill + getValue(`${prefix}:Goodwill`);
        setValue(
          `${prefix}:IntangibleAssetsAndGoodwill`,
          totalIntangibleAssetsAndGoodwill,
        );
      }

      // #endregion Intangible assets and goodwill

      /*
        Investments in Subsidiaries - MFRS and MPERS Calculations
      */
      // #region Investments in Subsidiaries

      // MPERS ONLY: Simple Investments in Subsidiaries Calculation
      if (prefix === 'ifrs-smes') {
        const totalInvestmentsInSubsidiaries =
          getValue(
            `${customPrefix}:InvestmentsInSubsidiariesUnquotedSharesNetOfImpairmentLosses`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInSubsidiariesQuotedSharesInMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInSubsidiariesQuotedSharesOutsideMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInSubsidiariesOtherInvestmentsNetOfImpairmentLosses`,
          );
        // Intentional typo "Subisidiaries" — must match the SSM MPERS taxonomy element exactly or
        // Arelle stops matching this total. Do NOT "correct" to InvestmentInSubsidiaries (MPERS-only;
        // reverted in c933312). See docs/specifications/sheets/09b_sub-classification-assets-liabilities-equity-mpers/04_investments-subsidiaries.md
        setValue(
          `${customPrefix}:InvestmentInSubisidiaries`,
          totalInvestmentsInSubsidiaries,
        );
      }

      // MFRS ONLY: Investments in Subsidiaries (with additional fields)
      if (prefix === 'ifrs-full') {
        const totalInvestmentsInSubsidiaries =
          getValue(
            `${customPrefix}:InvestmentInSubsidiariesUnquotedSharesInMalaysiaNetOfImpairmentLosses`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInSubsidiariesQuotedSharesInMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInSubsidiariesQuotedSharesOutsideMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInSubsidiariesFairValueAdjustmentsOnLoansAndAdvancesAndFinancialGuarantee`,
          ) +
          getValue(
            `${customPrefix}:ContributionsToSubsidiariesNetOfImpairmentLosses`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInSubsidiariesOtherInvestmentsNetOfImpairmentLosses`,
          );
        setValue(
          `${prefix}:InvestmentsInSubsidiaries`,
          totalInvestmentsInSubsidiaries,
        );
      }
      // #endregion Investments in Subsidiaries

      /*
        Investments in associates - MFRS and MPERS Calculations
      */
      // #region Investments in associates

      // MPERS
      if (prefix === 'ifrs-smes') {
        const totalInvestmentsInAssociates =
          getValue(
            `${customPrefix}:InvestmentsInAssociatesUnquotedSharesNetOfImpairmentLosses`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInAssociatesQuotedSharesInMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInAssociatesQuotedSharesOutsideMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInAssociatesShareOfPostAcquisitionProfitsAndReserves`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInAssociatesOtherInvestmentsNetOfImpairmentLosses`,
          );
        setValue(
          `${prefix}:InvestmentsInAssociates`,
          totalInvestmentsInAssociates,
        );
      }

      // MFRS
      if (prefix === 'ifrs-full') {
        const totalInvestmentsInAssociates =
          getValue(
            `${customPrefix}:InvestmentInAssociatesUnquotedSharesInMalaysiaNetOfImpairmentLosses`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInAssociatesQuotedSharesInMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInAssociatesQuotedSharesOutsideMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInAssociatesFairValueAdjustmentsOnLoansAndAdvancesAndFinancialGuarantee`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInAssociatesShareOfPostAcquisitionProfitsAndReserves`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInAssociatesUnrealisedProfitOnTransactionsWithAssociates`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInAssociatesOtherInvestmentsNetOfImpairmentLosses`,
          );
        setValue(
          `${prefix}:InvestmentsInAssociates`,
          totalInvestmentsInAssociates,
        );
      }
      // #endregion Investments in associates

      /*
        Investments in Joint Ventures - MFRS and MPERS Calculations
      */
      // #region Investments in Joint Ventures

      // MPERS
      if (prefix === 'ifrs-smes') {
        const totalInvestmentsInJointVentures =
          getValue(
            `${customPrefix}:InvestmentsInJointVentureUnquotedSharesNetOfImpairmentLosses`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInJointVenturesQuotedSharesInMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInJointVenturesQuotedSharesOutsideMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInJointVentureShareOfPostAcquisitionProfitsAndReserves`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInJointVentureOtherInvestment`,
          );
        setValue(
          `${prefix}:InvestmentsInJointVentures`,
          totalInvestmentsInJointVentures,
        );
      }

      // MFRS
      if (prefix === 'ifrs-full') {
        const totalInvestmentsInJointVentures =
          getValue(
            `${customPrefix}:InvestmentInJointVenturesUnquotedSharesInMalaysiaNetOfImpairmentLosses`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInJointVenturesQuotedSharesInMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInJointVenturesQuotedSharesOutsideMalaysia`,
          ) +
          getValue(
            `${customPrefix}:InvestmentInJointVenturesFairValueAdjustmentsOnLoansAdvancesAndFinancialGuarantee`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInJointVentureShareOfPostAcquisitionProfitsAndReserves`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInJointVentureUnrealisedProfitOnTransactionsWithJointVentures`,
          ) +
          getValue(
            `${customPrefix}:InvestmentsInJointVentureOtherInvestment`,
          );
        setValue(
          `${prefix}:InvestmentsInJointVentures`,
          totalInvestmentsInJointVentures,
        );
      }

      // #endregion Investments in Joint Ventures

      /*
        Trade and other non-current receivables - MFRS and MPERS Calculations
      */
      // #region Trade and Other Non-current Receivables

      // MPERS
      if (prefix === 'ifrs-smes') {
        // 1. Total non-current trade receivables (Level 4 total)
        const totalNoncurrentTradeReceivables =
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromHoldingCompany`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromRelatedParties`,
          ) +
          getValue(`${customPrefix}:OtherNoncurrentTradeReceivables`);
        setValue(
          `${customPrefix}:NoncurrentTradeReceivables`,
          totalNoncurrentTradeReceivables,
        );

        // 2. Total other non-current receivables due from related parties
        const totalOtherNoncurrentReceivablesDueFromRelatedParties =
          getValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromHoldingCompany`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromOtherRelatedParties`,
          );
        setValue(
          `${customPrefix}:OtherNoncurrentReceivablesDueFromRelatedParties`,
          totalOtherNoncurrentReceivablesDueFromRelatedParties,
        );

        // 3. Total non-current non-trade receivables
        const totalNoncurrentNontradeReceivables =
          getValue(
            `${customPrefix}:OtherNoncurrentNontradePrepaymentAndAccruedIncome`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentNontradeLeaseAndHirePurchaseReceivables`,
          ) +
          getValue(`${customPrefix}:OtherNoncurrentNontradeMiscellaneous`);
        setValue(
          `${customPrefix}:OtherNoncurrentNontradeReceivables`,
          totalNoncurrentNontradeReceivables,
        );

        // 4. Total other non-current receivables
        // MPERS: equals the non-trade total only (the related-parties subtotal
        // is still shown but excluded here per MBRS MPERS spec — see f63 formula
        // in 09b .../07_trade-and-other-non-current-receivables.md).
        const totalOtherNoncurrentReceivables =
          totalNoncurrentNontradeReceivables;
        setValue(
          `${customPrefix}:OtherNoncurrentReceivables`,
          totalOtherNoncurrentReceivables,
        );

        // 5. Total trade and other non-current receivables (Level 3 total)
        // MPERS: trade + the related-parties subtotal (f58, excluded from f63)
        // + other non-current receivables (f63). See f64 formula in
        // 09b .../07_trade-and-other-non-current-receivables.md.
        const totalTradeAndOtherNoncurrentReceivables =
          totalNoncurrentTradeReceivables +
          totalOtherNoncurrentReceivablesDueFromRelatedParties +
          totalOtherNoncurrentReceivables;
        setValue(
          `${prefix}:NoncurrentReceivables`,
          totalTradeAndOtherNoncurrentReceivables,
        );
      }

      // MFRS
      if (prefix === 'ifrs-full') {
        // 1. Total non-current trade receivables (Level 4 total)
        const totalNoncurrentTradeReceivables =
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromCustomers`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromHoldingCompany`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentTradeReceivablesDueFromRelatedParties`,
          ) +
          getValue(`${customPrefix}:OtherNoncurrentTradeReceivables`);
        setValue(
          `${prefix}:NoncurrentTradeReceivables`,
          totalNoncurrentTradeReceivables,
        );

        // 2. Total other non-current receivables due from related parties
        const totalOtherNoncurrentReceivablesDueFromRelatedParties =
          getValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromHoldingCompany`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:MiscellaneousOtherNoncurrentReceivablesDueFromRelatedCompanies`,
          );
        setValue(
          `${customPrefix}:OtherNoncurrentReceivablesDueFromRelatedParties`,
          totalOtherNoncurrentReceivablesDueFromRelatedParties,
        );

        // 3. Total non-current non-trade receivables
        const totalNoncurrentNontradeReceivables =
          getValue(
            `${customPrefix}:OtherNoncurrentNontradePrepaymentAndAccruedIncome`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentNontradeLeaseAndHirePurchaseReceivables`,
          ) +
          getValue(`${customPrefix}:OtherNoncurrentNontradeMiscellaneous`);
        setValue(
          `${customPrefix}:OtherNoncurrentNontradeReceivables`,
          totalNoncurrentNontradeReceivables,
        );

        // 4. Total other non-current receivables (sum of Level 5 totals)
        const totalOtherNoncurrentReceivables =
          totalOtherNoncurrentReceivablesDueFromRelatedParties +
          totalNoncurrentNontradeReceivables;
        setValue(
          `${prefix}:OtherNoncurrentReceivables`,
          totalOtherNoncurrentReceivables,
        );

        // 5. Total trade and other non-current receivables (Level 3 total)
        const totalTradeAndOtherNoncurrentReceivables =
          totalNoncurrentTradeReceivables + totalOtherNoncurrentReceivables;
        setValue(
          `${prefix}:NoncurrentReceivables`,
          totalTradeAndOtherNoncurrentReceivables,
        );

        // 6. Total non-current derivatives at fair value through profit or loss (Level 4 total)
        const totalNoncurrentDerivativesFVTPL =
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialAssetsForwardContract`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialAssetsOptions`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialAssetsSwaps`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentOtherDerivativeFinancialAssetsAtFairValueThroughProfitOrLoss`,
          );
        setValue(
          `${customPrefix}:NoncurrentDerivativeFinancialAssetsAtFairValueThroughProfitOrLoss`,
          totalNoncurrentDerivativesFVTPL,
        );

        // 7. Total non-current derivative financial assets (Level 3 total)
        const totalNoncurrentDerivativeFinancialAssets =
          totalNoncurrentDerivativesFVTPL +
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialAssetsOthers`,
          );
        setValue(
          `${prefix}:NoncurrentDerivativeFinancialAssets`,
          totalNoncurrentDerivativeFinancialAssets,
        );
      }

      // #endregion Trade and Other Non-current Receivables

      /*
        Inventories - MFRS and MPERS Calculations
      */
      // #region Inventories

      // MPERS
      if (prefix === 'ifrs-smes') {
        const totalInventories =
          getValue(`${customPrefix}:RawMaterials`) +
          getValue(`${prefix}:WorkInProgress`) +
          getValue(`${customPrefix}:FinishedGoods`) +
          getValue(`${customPrefix}:SpareParts`) +
          getValue(`${customPrefix}:OtherInventories`);
        setValue(`${prefix}:InventoriesTotal`, totalInventories);
      }

      // MFRS
      if (prefix === 'ifrs-full') {
        const totalInventories =
          getValue(`${prefix}:RawMaterials`) +
          getValue(`${prefix}:WorkInProgress`) +
          getValue(`${prefix}:FinishedGoods`) +
          getValue(`${prefix}:SpareParts`) +
          getValue(`${prefix}:OtherInventories`);
        setValue(`${prefix}:InventoriesTotal`, totalInventories);
      }

      // #endregion Inventories

      /*
        Trade and Other Current Receivables - MFRS and MPERS Calculations
      */
      // #region Trade and Other Current Receivables
      // MPERS
      if (prefix === 'ifrs-smes') {
        // 1. Total current trade receivables (Level 4 total)
        const totalCurrentTradeReceivables =
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromContractCustomers`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromHoldingCompany`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromRelatedParties`,
          ) +
          getValue(`${customPrefix}:OtherCurrentTradeReceivables`);
        setValue(
          `${customPrefix}:CurrentTradeReceivables`,
          totalCurrentTradeReceivables,
        );

        // 2. Total current other receivables due from related parties (Level 5 total)
        const totalOtherCurrentReceivablesDueFromRelatedParties =
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromHoldingCompany`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromOtherRelatedParties`,
          );
        setValue(
          `${customPrefix}:OtherCurrentReceivablesDueFromRelatedParties`,
          totalOtherCurrentReceivablesDueFromRelatedParties,
        );

        // 3. Total current prepayments and current accrued income (Level 5 total)
        const totalCurrentPrepaymentsAndAccruedIncome =
          getValue(`${customPrefix}:OtherCurrentPrepayments`) +
          getValue(`${customPrefix}:OtherCurrentAccruedIncome`);
        setValue(
          `${customPrefix}:OtherCurrentPrepaymentsAndCurrentAccruedIncome`,
          totalCurrentPrepaymentsAndAccruedIncome,
        );

        // 4. Total non-trade receivables (Level 5 total)
        const totalCurrentNontradeReceivables =
          getValue(
            `${customPrefix}:OtherCurrentNontradeFinanceIncomeReceivable`,
          ) +
          getValue(`${customPrefix}:OtherCurrentNontradeDeposits`) +
          getValue(
            `${customPrefix}:OtherCurrentNontradeDividendReceivables`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentNontradeLeaseAndHirePurchaseReceivables`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentMiscellaneousNontradeReceivables`,
          );
        setValue(
          `${customPrefix}:OtherCurrentNontradeReceivables`,
          totalCurrentNontradeReceivables,
        );

        // 5. Total other current receivables (Level 4 total)
        const totalOtherCurrentReceivables =
          totalOtherCurrentReceivablesDueFromRelatedParties +
          totalCurrentPrepaymentsAndAccruedIncome +
          totalCurrentNontradeReceivables;
        setValue(
          `${customPrefix}:OtherCurrentReceivables`,
          totalOtherCurrentReceivables,
        );

        // 6. Total trade and other current receivables (Level 3 total)
        const totalTradeAndOtherCurrentReceivables =
          totalCurrentTradeReceivables + totalOtherCurrentReceivables;
        setValue(
          `${prefix}:TradeAndOtherCurrentReceivables`,
          totalTradeAndOtherCurrentReceivables,
        );
      }

      // MFRS
      if (prefix === 'ifrs-full') {
        // 1. Total current trade receivables (Level 4 total)
        const totalCurrentTradeReceivables =
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromCustomers`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromContractCustomers`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromHoldingCompany`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:CurrentTradeReceivablesDueFromRelatedParties`,
          ) +
          getValue(
            `${customPrefix}:TradeReceivablesUnearnedCurrentCarryingCharges`,
          ) +
          getValue(`${customPrefix}:OtherCurrentTradeReceivables`);
        setValue(
          `${prefix}:CurrentTradeReceivables`,
          totalCurrentTradeReceivables,
        );

        // 2. Total other current receivables due from related parties (Level 5 total)
        const totalOtherCurrentReceivablesDueFromRelatedParties =
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromHoldingCompany`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromOtherRelatedParties`,
          );
        setValue(
          `${customPrefix}:OtherCurrentReceivablesDueFromRelatedParties`,
          totalOtherCurrentReceivablesDueFromRelatedParties,
        );

        // 3. Total current prepayments and current accrued income (Level 5 total)
        const totalCurrentPrepaymentsAndAccruedIncome =
          getValue(`${customPrefix}:OtherCurrentPrepayments`) +
          getValue(`${customPrefix}:OtherCurrentAccruedIncome`);
        setValue(
          `${customPrefix}:OtherCurrentPrepaymentsAndCurrentAccruedIncome`,
          totalCurrentPrepaymentsAndAccruedIncome,
        );

        // 4. Total current non-trade receivables (Level 5 total)
        const totalCurrentNontradeReceivables =
          getValue(
            `${customPrefix}:OtherCurrentNontradeFinanceIncomeReceivable`,
          ) +
          getValue(`${customPrefix}:OtherCurrentNontradeDeposits`) +
          getValue(
            `${customPrefix}:OtherCurrentNontradeDividendReceivables`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentNontradeLeaseAndHirePurchaseReceivables`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentMiscellaneousNontradeReceivables`,
          );
        setValue(
          `${customPrefix}:OtherCurrentNontradeReceivables`,
          totalCurrentNontradeReceivables,
        );

        // 5. Total other current receivables (Level 4 total)
        const totalOtherCurrentReceivables =
          totalOtherCurrentReceivablesDueFromRelatedParties +
          totalCurrentPrepaymentsAndAccruedIncome +
          totalCurrentNontradeReceivables;
        setValue(
          `${prefix}:OtherCurrentReceivables`,
          totalOtherCurrentReceivables,
        );

        // 6. Total trade and other current receivables (Level 3 total)
        const totalTradeAndOtherCurrentReceivables =
          totalCurrentTradeReceivables + totalOtherCurrentReceivables;
        setValue(
          `${prefix}:TradeAndOtherCurrentReceivables`,
          totalTradeAndOtherCurrentReceivables,
        );

        // 7. Total current derivatives at fair value through profit or loss (Level 4 total)
        const totalCurrentDerivativesFVTPL =
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialAssetsForwardContract`,
          ) +
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialAssetsOptions`,
          ) +
          getValue(`${customPrefix}:CurrentDerivativeFinancialAssetsSwaps`) +
          getValue(
            `${customPrefix}:CurrentOtherDerivativeFinancialAssetsAtFairValueThroughProfitOrLoss`,
          );
        setValue(
          `${customPrefix}:CurrentDerivativeFinancialAssetsAtFairValueThroughProfitOrLoss`,
          totalCurrentDerivativesFVTPL,
        );

        // 8. Total current derivative financial assets (Level 3 total)
        const totalCurrentDerivativeFinancialAssets =
          totalCurrentDerivativesFVTPL +
          getValue(`${customPrefix}:CurrentDerivativeFinancialAssetsOthers`);
        setValue(
          `${prefix}:CurrentDerivativeFinancialAssets`,
          totalCurrentDerivativeFinancialAssets,
        );
      }

      // #endregion Trade and Other Current Receivables

      /*
        Cash and cash equivalents - MFRS and MPERS Calculations
      */
      // #region Cash and Cash Equivalents

      // 1. Total cash (Level 4 total)
      const totalCash =
        getValue(`${prefix}:CashOnHand`) +
        getValue(`${prefix}:BalancesWithBanks`);
      setValue(`${prefix}:Cash`, totalCash);

      // 2. Total cash equivalents (Level 4 total)
      // MPERS: 7-field calculation
      // MFRS: 6-field calculation
      const totalCashEquivalents =
        prefix === 'ifrs-full'
          ? getValue(
              `${customPrefix}:DepositsAndPlacementsWithLicensedBanks`,
            ) +
            getValue(`${customPrefix}:DepositsPlacedWithOtherCorporations`) +
            getValue(
              `${customPrefix}:CashEquivalentsWithOtherFinancialInstitutions`,
            ) +
            getValue(
              `${prefix}:ShorttermDepositsClassifiedAsCashEquivalents`,
            ) +
            getValue(
              `${prefix}:ShorttermInvestmentsClassifiedAsCashEquivalents`,
            ) +
            getValue(
              `${prefix}:BankingArrangementsClassifiedAsCashEquivalents`,
            )
          : getValue(
              `${customPrefix}:DepositsAndPlacementsWithLicensedBanks`,
            ) +
            getValue(`${customPrefix}:DepositsPlacedWithOtherCorporations`) +
            getValue(
              `${customPrefix}:FixedDepositsWithFinancialInstitutions`,
            ) +
            getValue(
              `${customPrefix}:CashEquivalentsWithOtherFinancialInstitutions`,
            ) +
            getValue(
              `${prefix}:ShorttermDepositsClassifiedAsCashEquivalents`,
            ) +
            getValue(
              `${prefix}:ShorttermInvestmentsClassifiedAsCashEquivalents`,
            ) +
            getValue(
              `${prefix}:BankingArrangementsClassifiedAsCashEquivalents`,
            );
      setValue(`${prefix}:CashEquivalents`, totalCashEquivalents);

      // 3. Total cash and cash equivalents (Level 3 total)
      const totalCashAndCashEquivalents =
        totalCash +
        totalCashEquivalents +
        getValue(`${prefix}:OtherCashAndCashEquivalents`);
      setValue(
        `${specialPrefix}:CashAndBankBalances`,
        totalCashAndCashEquivalents,
      );

      // MFRS ONLY: Other Non-current Assets
      if (prefix === 'ifrs-full') {
        const totalOtherNoncurrentAssets =
          getValue(`${customPrefix}:PrepaidRentalForBuildingAndFacilities`) +
          getValue(`${customPrefix}:PrepaidLandLease`) +
          getValue(`${customPrefix}:MiscellaneousOtherNoncurrentAssets`);
        setValue(
          `${prefix}:OtherNoncurrentAssets`,
          totalOtherNoncurrentAssets,
        );
      }

      // #endregion Cash and Cash Equivalents

      /*
        Issued capital - MFRS and MPERS Calculations
      */
      // #region Issued Capital

      const totalIssuedCapital =
        getValue(`${customPrefix}:CapitalFromOrdinaryShares`) +
        getValue(`${customPrefix}:CapitalFromRedeemablePreferenceShares`) +
        getValue(`${customPrefix}:CapitalFromNonredeemablePreferenceShares`);
      setValue(`${prefix}:IssuedCapital`, totalIssuedCapital);

      // #endregion Issued Capital

      /*
        Reserves - MFRS and MPERS Calculations
      */
      // #region Reserves

      // 1. Total non-distributable reserves (Level 4 total)
      // MPERS: 4-field calculation
      // MFRS: Full calculation with all fields
      const totalNondistributableReserves =
        prefix === 'ifrs-smes'
          ? getValue(`${customPrefix}:CapitalReserve`) +
            getValue(`${prefix}:ReserveOfExchangeDifferencesOnTranslation`) +
            getValue(`${prefix}:RevaluationSurplus`) +
            getValue(`${customPrefix}:OtherNondistributableReserves`)
          : getValue(`${prefix}:CapitalReserve`) +
            getValue(`${prefix}:ReserveOfCashFlowHedges`) +
            getValue(`${prefix}:ReserveOfExchangeDifferencesOnTranslation`) +
            getValue(`${prefix}:ReserveOfSharebasedPayments`) +
            getValue(`${prefix}:RevaluationSurplus`) +
            getValue(`${prefix}:StatutoryReserve`) +
            getValue(`${prefix}:WarrantReserve`) +
            getValue(`${customPrefix}:OtherNondistributableReserves`);
      setValue(
        `${customPrefix}:NondistributableReserves`,
        totalNondistributableReserves,
      );

      // 2. Total distributable reserves (Level 4 total)
      // MPERS: Only 1 field
      // MFRS: Full calculation with all fields
      const totalDistributableReserves =
        prefix === 'ifrs-smes'
          ? getValue(`${customPrefix}:OtherDistributableReserve`)
          : getValue(`${customPrefix}:FairValueReserves`) +
            getValue(
              `${customPrefix}:ReserveOfNoncurrentAssetsClassifiedAsHeldForSale`,
            ) +
            getValue(`${customPrefix}:ConsolidatedReserve`) +
            getValue(`${customPrefix}:WarrantyReserve`) +
            getValue(`${customPrefix}:OtherDistributableReserve`);
      setValue(
        prefix === 'ifrs-smes'
          ? `${customPrefix}:DistributableReseves`
          : `${customPrefix}:DistributableReserve`,
        totalDistributableReserves,
      );

      // 3. Total reserves (Level 3 total)
      const totalReserves =
        totalNondistributableReserves + totalDistributableReserves;
      setValue(`${prefix}:OtherReserves`, totalReserves);

      // #endregion Reserves

      /*
        Equity - others components - MFRS and MPERS Calculations
      */
      // #region Equity - others components

      // 1. Total equity - other components
      // MPERS: 4 fields (no Head Office Accounts)
      // MFRS: 5 fields
      const totalEquityOtherComponents =
        prefix === 'ifrs-smes'
          ? getValue(`${customPrefix}:PerpetualSukuk`) +
            getValue(
              `${customPrefix}:EquityComponentOfIrredeemableConvertibleUnsecuredLoanStocks`,
            ) +
            getValue(`${customPrefix}:EquityComponentOfPreferenceShares`) +
            getValue(
              `${customPrefix}:EquityComponentsOfOtherFinancialInstruments`,
            )
          : getValue(`${customPrefix}:PerpetualSukuk`) +
            getValue(
              `${customPrefix}:EquityComponentOfIrredeemableConvertibleUnsecuredLoanStocks`,
            ) +
            getValue(`${customPrefix}:EquityComponentOfPreferenceShares`) +
            getValue(`${customPrefix}:HeadOfficeAccounts`) +
            getValue(
              `${customPrefix}:EquityComponentsOfOtherFinancialInstruments`,
            );
      setValue(
        `${customPrefix}:OtherComponentsOfEquity`,
        totalEquityOtherComponents,
      );

      // #endregion Equity - others components

      /*
        Loans and Borrowings - MFRS and MPERS Calculations
      */
      // #region Loans and Borrowings

      // 1. Total non-current loans and borrowings
      if (prefix === 'ifrs-smes') {
        // MPERS: Simplified flat structure (14 fields)
        const totalNoncurrentBorrowings =
          getValue(
            `${customPrefix}:NoncurrentPortionOfNoncurrentSecuredBankLoansReceived`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfNoncurrentUnsecuredBankLoansReceived`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfSecuredConvertibleNotes`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfUnsecuredConvertibleNotes`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentPortionOfRedeemablePreferenceShares`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfFinanceLeaseLiabilities`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfLoanFromHoldingCompany`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentPortionOfLoanFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentPortionOfLoanFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentPortionOfLoanFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfLoanFromOtherRelatedCompanies`,
          ) +
          getValue(`${customPrefix}:OtherNoncurrentSecuredBankLoans`) +
          getValue(`${customPrefix}:OtherNoncurrentUnsecuredBankLoans`) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfOtherNoncurrentBorrowings`,
          );
        setValue(
          `${customPrefix}:NoncurrentBorrowings`,
          totalNoncurrentBorrowings,
        );
      } else {
        // MFRS: Hierarchical structure with Level 4 totals
        // 1. Total non-current portion of non-current secured bank loans received (Level 4 total)
        const totalNoncurrentSecuredBankLoans =
          getValue(`${customPrefix}:NoncurrentPortionOfSecuredTermLoans`) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfSecuredIslamicFinancingFacilities`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfSecuredHirePurchaseAndFinanceLeaseLiabilities`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfSecuredIslamicMediumTermNotes`,
          ) +
          getValue(`${customPrefix}:NoncurrentPortionOfSecuredSukuk`) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfSecuredRevolvingCreditAndOthers`,
          ) +
          getValue(`${customPrefix}:OtherNoncurrentSecuredBankLoans`);
        setValue(
          `${prefix}:NoncurrentPortionOfNoncurrentSecuredBankLoansReceived`,
          totalNoncurrentSecuredBankLoans,
        );

        // 2. Total non-current portion of non-current unsecured bank loans received (Level 4 total)
        const totalNoncurrentUnsecuredBankLoans =
          getValue(`${customPrefix}:NoncurrentPortionOfUnsecuredTermLoans`) +
          getValue(
            `${customPrefix}:NoncurrentPortionofUnsecuredIrredeemableConvertibleUnsecuredLoanStocks`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfUnsecuredIslamicFinancingFacilities`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfUnsecuredHirePurchaseAndFinanceLeaseLiabilities`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfUnsecuredIslamicMediumTermNotes`,
          ) +
          getValue(`${customPrefix}:NoncurrentPortionOfUnsecuredSukuk`) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfUnsecuredRevolvingCreditAndOthers`,
          ) +
          getValue(`${customPrefix}:OtherNoncurrentUnsecuredBankLoans`);
        setValue(
          `${prefix}:NoncurrentPortionOfNoncurrentUnsecuredBankLoansReceived`,
          totalNoncurrentUnsecuredBankLoans,
        );

        // 3. Total non-current portion of secured bonds/sukuk/loan stocks (Level 4 total)
        const totalSecuredBondsSukukLoanStock =
          getValue(`${customPrefix}:NoncurrentPortionOfSecuredBonds`) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfSecuredSukukAsPerShariahCommittee`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfSecuredMediumTermNotes`,
          ) +
          getValue(`${customPrefix}:NoncurrentPortionOfSecuredLoanStocks`) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfSecuredPreferenceShares`,
          );
        setValue(
          `${customPrefix}:NoncurrentPortionOfSecuredBondsSukukAndLoanStock`,
          totalSecuredBondsSukukLoanStock,
        );

        // 4. Total non-current portion of unsecured bonds/sukuk/loan stocks (Level 4 total)
        const totalUnsecuredBondsSukukLoanStock =
          getValue(`${customPrefix}:NoncurrentPortionOfUnsecuredBonds`) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfUnsecuredSukukAsPerShariahCommittee`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfUnsecuredMediumTermNotes`,
          ) +
          getValue(`${customPrefix}:NoncurrentPortionOfUnsecuredLoanStocks`) +
          getValue(
            `${customPrefix}:NoncurrentPortionOfUnsecuredPreferenceShares`,
          );
        setValue(
          `${customPrefix}:NoncurrentPortionOfUnsecuredBondsSukukAndLoanStock`,
          totalUnsecuredBondsSukukLoanStock,
        );

        // 5. Total other non-current borrowings (Level 4 total)
        const totalOtherNoncurrentBorrowings =
          getValue(
            `${customPrefix}:OtherNoncurrentPortionOfLoanFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentPortionOfLoanFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentPortionOfLoanFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentPortionOfRedeemablePreferenceShares`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentPortionOfMiscellaneousBorrowings`,
          );
        setValue(
          `${prefix}:NoncurrentPortionOfOtherNoncurrentBorrowings`,
          totalOtherNoncurrentBorrowings,
        );

        // 6. Total non-current borrowings (Level 3 total - sum of all 5 Level 4 totals)
        const totalNoncurrentBorrowings =
          totalNoncurrentSecuredBankLoans +
          totalNoncurrentUnsecuredBankLoans +
          totalSecuredBondsSukukLoanStock +
          totalUnsecuredBondsSukukLoanStock +
          totalOtherNoncurrentBorrowings;
        setValue(`${prefix}:LongtermBorrowings`, totalNoncurrentBorrowings);
      }

      // #endregion Loans and Borrowings

      /*
        Non-current employee benefits - MFRS and MPERS Calculations
      */
      // #region Non-current employee benefits

      // MPERS: 6 fields (no Retirement Benefits) vs MFRS: 7 fields
      const totalNoncurrentEmployeeBenefitLiabilities =
        prefix === 'ifrs-smes'
          ? getValue(
              `${customPrefix}:CashSettledShareBasedPaymentLiability`,
            ) +
            getValue(`${customPrefix}:EmploymentTerminationBenefits`) +
            getValue(`${customPrefix}:ProvisionForUnconsumedLeave`) +
            getValue(`${customPrefix}:DefinedContributionPlan`) +
            getValue(`${customPrefix}:DefinedBenefitPlan`) +
            getValue(`${customPrefix}:OtherEmployeeBenefitLiabilities`)
          : getValue(
              `${customPrefix}:NoncurrentCashSettledShareBasedPaymentLiability`,
            ) +
            getValue(`${customPrefix}:NoncurrentRetirementBenefits`) +
            getValue(
              `${customPrefix}:NoncurrentEmploymentTerminationBenefits`,
            ) +
            getValue(`${customPrefix}:NoncurrentProvisionForConsumedLeave`) +
            getValue(`${customPrefix}:NoncurrentDefinedContributionPlan`) +
            getValue(`${customPrefix}:NoncurrentDefinedBenefitPlan`) +
            getValue(
              `${customPrefix}:NoncurrentOtherEmployeeBenefitLiabilities`,
            );
      setValue(
        `${prefix}:NoncurrentProvisionsForEmployeeBenefits`,
        totalNoncurrentEmployeeBenefitLiabilities,
      );

      // #endregion Non-current employee benefits

      /*
        Non-current provisions - MFRS and MPERS Calculations
      */
      // #region Non-current provisions
      // MPERS: 6 fields (no Refund provision)
      // MFRS: 7 fields
      const totalNoncurrentProvisions =
        prefix === 'ifrs-smes'
          ? getValue(`${prefix}:LongtermWarrantyProvision`) +
            getValue(`${prefix}:LongtermRestructuringProvision`) +
            getValue(`${prefix}:LongtermLegalProceedingsProvision`) +
            getValue(`${prefix}:LongtermOnerousContractsProvision`) +
            getValue(
              `${customPrefix}:LongtermProvisionForDecommissioningRestorationAndRehabilitationCosts`,
            ) +
            getValue(`${prefix}:LongtermMiscellaneousOtherProvisions`)
          : getValue(`${prefix}:LongtermWarrantyProvision`) +
            getValue(`${prefix}:LongtermRestructuringProvision`) +
            getValue(`${prefix}:LongtermLegalProceedingsProvision`) +
            getValue(`${prefix}:NoncurrentRefundsProvision`) +
            getValue(`${prefix}:LongtermOnerousContractsProvision`) +
            getValue(
              `${prefix}:LongtermProvisionForDecommissioningRestorationAndRehabilitationCosts`,
            ) +
            getValue(`${prefix}:OtherLongtermProvisions`);
      setValue(`${prefix}:NoncurrentProvisions`, totalNoncurrentProvisions);

      //#endregion Non-current provisions

      /*
        Trade and Other Non-current Payables - MFRS and MPERS Calculations
      */
      // #region Trade and Other Non-current Payables

      // 1. Total non-current trade payables (Level 4 total)
      // MPERS: 6 fields (no generic Trade payables)
      // MFRS: 7 fields
      const totalNoncurrentTradePayables =
        prefix === 'ifrs-smes'
          ? getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToHoldingCompany`,
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToSubsidiaries`,
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToAssociates`,
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToJointVentures`,
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToOtherRelatedParties`,
            ) +
            getValue(`${customPrefix}:OtherNoncurrentTradePayables`)
          : getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToCustomers`,
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToHoldingCompany`,
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToSubsidiaries`,
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToAssociates`,
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToJointVentures`,
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradePayablesDueToOtherRelatedParties`,
            ) +
            getValue(`${customPrefix}:OtherNoncurrentTradePayables`);
      setValue(
        `${prefix}:NoncurrentPayablesToTradeSuppliers`,
        totalNoncurrentTradePayables,
      );

      // 2. Total other non-current payables due to related parties (Level 5 total)
      const totalOtherNoncurrentPayablesDueToRelatedParties =
        getValue(
          `${customPrefix}:OtherNoncurrentTradePayablesDueToHoldingCompany`,
        ) +
        getValue(
          `${customPrefix}:OtherNoncurrentTradePayablesDueToSubsidiaries`,
        ) +
        getValue(
          `${customPrefix}:OtherNoncurrentTradePayablesDueToAssociates`,
        ) +
        getValue(
          `${customPrefix}:OtherNoncurrentTradePayablesDueToJointVentures`,
        ) +
        getValue(
          `${customPrefix}:OtherNoncurrentPayablesDueToRelatedCompanies`,
        );
      setValue(
        `${customPrefix}:OtherNoncurrentPayablesDueToRelatedParties`,
        totalOtherNoncurrentPayablesDueToRelatedParties,
      );

      // 3. Total other payables due to non-controlling interests (Level 5 total)
      // MFRS only - MPERS does not have this section
      let totalOtherPayablesDueToNoncontrollingInterests = 0;
      if (prefix === 'ifrs-full') {
        totalOtherPayablesDueToNoncontrollingInterests =
          getValue(
            `${customPrefix}:OtherNoncurrentDividendPayablesDueToNoncontrollingInterests`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentLoansFromNoncontrollingInterests`,
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentMiscellaneousPayablesDueToNoncontrollingInterests`,
          );
        setValue(
          `${customPrefix}:OtherNoncurrentPayablesDueToNoncontrollingInterests`,
          totalOtherPayablesDueToNoncontrollingInterests,
        );
      }

      // 4. Total non-current non-trade payables (Level 5 total)
      // MPERS: 4 fields (no Deposits) vs MFRS: 5 fields
      const totalNoncurrentNontradePayables =
        prefix === 'ifrs-smes'
          ? getValue(`${customPrefix}:NoncurrentNontradeDeferredIncome`) +
            getValue(`${customPrefix}:NoncurrentNontradeAccruals`) +
            getValue(`${customPrefix}:NoncurrentNontradeRetentionPayables`) +
            getValue(`${customPrefix}:OtherNoncurrentNontradePayables`)
          : getValue(`${customPrefix}:NoncurrentNontradeDeferredIncome`) +
            getValue(`${customPrefix}:NoncurrentNontradeAccruals`) +
            getValue(`${customPrefix}:NoncurrentNontradeRetentionPayables`) +
            getValue(
              `${customPrefix}:NoncurrentNontradeDepositsAndAdvancedBillings`,
            ) +
            getValue(`${customPrefix}:OtherNoncurrentNontradePayables`);
      setValue(
        `${customPrefix}:NoncurrentNontradePayables`,
        totalNoncurrentNontradePayables,
      );

      // 5. Total other non-current payables (Level 4 total)
      // MPERS: sum of 2 Level 5 totals (no non-controlling interests)
      // MFRS: sum of 3 Level 5 totals
      const totalOtherNoncurrentPayables =
        prefix === 'ifrs-smes'
          ? totalOtherNoncurrentPayablesDueToRelatedParties +
            totalNoncurrentNontradePayables
          : totalOtherNoncurrentPayablesDueToRelatedParties +
            totalOtherPayablesDueToNoncontrollingInterests +
            totalNoncurrentNontradePayables;
      setValue(
        prefix === 'ifrs-smes'
          ? `${customPrefix}:OtherNoncurrentPayables`
          : `${prefix}:OtherNoncurrentPayables`,
        totalOtherNoncurrentPayables,
      );

      // 6. Total trade and other non-current payables (Level 3 total - sum of 2 Level 4 totals)
      const totalTradeAndOtherNoncurrentPayables =
        totalNoncurrentTradePayables + totalOtherNoncurrentPayables;
      setValue(
        `${prefix}:NoncurrentPayables`,
        totalTradeAndOtherNoncurrentPayables,
      );

      // 7. MFRS only - MPERS does not have this section
      if (prefix === 'ifrs-full') {
        // Total non-current derivatives at fair value through profit or loss (Level 4 total)
        const totalNoncurrentDerivativeLiabilitiesFVTPL =
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesForwardContract`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesOptions`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesSwaps`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesOtherDerivatives`,
          );
        setValue(
          `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesAtFairValueThroughProfitOrLoss`,
          totalNoncurrentDerivativeLiabilitiesFVTPL,
        );

        // Total non-current derivatives used for hedging (Level 4 total)
        const totalNoncurrentDerivativeLiabilitiesHedging =
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesForwardContractUsedForHedging`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitesOptionsUsedForHedging`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesSwapUsedForHedging`,
          ) +
          getValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesOtherDerivativesUsedForHedging`,
          );
        setValue(
          `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesUsedForHedging`,
          totalNoncurrentDerivativeLiabilitiesHedging,
        );

        // Total non-current derivative financial liabilities (Level 3 total)
        const totalNoncurrentDerivativeFinancialLiabilities =
          totalNoncurrentDerivativeLiabilitiesFVTPL +
          totalNoncurrentDerivativeLiabilitiesHedging +
          getValue(
            `${customPrefix}:OtherNoncurrentDerivativeFinancialLiabilities`,
          );
        setValue(
          `${prefix}:NoncurrentDerivativeFinancialLiabilities`,
          totalNoncurrentDerivativeFinancialLiabilities,
        );
      }

      // #endregion Trade and Other Non-current Payables

      /*
        Current Borrowings - MFRS and MPERS Calculations
      */
      // #region Current Borrowings

      if (prefix === 'ifrs-smes') {
        // 1. Total current loans and borrowings
        // MPERS: Simplified flat structure (15 fields)
        const totalCurrentBorrowings =
          getValue(
            `${customPrefix}:CurrentSecuredBankLoansReceivedAndCurrentPortionOfNoncurrentSecuredBankLoansReceived`,
          ) +
          getValue(
            `${customPrefix}:CurrentUnsecuredBankLoansReceivedAndCurrentPortionOfNoncurrentUnsecuredBankLoansReceived`,
          ) +
          getValue(`${customPrefix}:CurrenPortionOfSecuredConvertibleNotes`) +
          getValue(
            `${customPrefix}:CurrentPortionOfUnsecuredConvertibleNotes`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredBankOverdrafts`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredBankOverdrafts`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfRedeemablePreferenceShares`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfFinanceLeaseLiabilities`,
          ) +
          getValue(`${customPrefix}:CurrentPortionOfLoanFromHoldingCompany`) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfLoanFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfLoanFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfLoanFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfLoanFromOtherRelatedCompanies`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredOtherBankLoanReceived`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredOtherBankLoansReceived`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfMiscellaneousBorrowings`,
          );
        setValue(`${prefix}:ShorttermBorrowings`, totalCurrentBorrowings);
      } else {
        // MFRS: Hierarchical structure with Level 4 totals
        // 1. Total current secured bank loans received and current portion (Level 4 total)
        const totalCurrentSecuredBankLoans =
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredBankersAcceptance`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredBankOverdrafts`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredTradeFinancingFacilities`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredTermLoans`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredIslamicFinancingFacilities`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredHirePurchaseAndFinanceLeaseLiabilities`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredIslamicMediumTermNotes`,
          ) +
          getValue(`${customPrefix}:CurrentPortionOfNoncurrentSecuredSukuk`) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredRevolvingCreditAndOthers`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentSecuredOtherBankLoanReceived`,
          );
        setValue(
          `${prefix}:CurrentSecuredBankLoansReceivedAndCurrentPortionOfNoncurrentSecuredBankLoansReceived`,
          totalCurrentSecuredBankLoans,
        );

        // 2. Total current unsecured bank loans received and current portion (Level 4 total)
        const totalCurrentUnsecuredBankLoans =
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredBlockDiscountingPayables`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredTermLoans`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredIrredeemableConvertibleUnsecuredLoanStocks`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredIslamicFinancingFacilities`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredHirePurchaseAndFinanceLeaseLiabilities`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredIslamicMediumTermNotes`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredSukuk`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredRevolvingCreditAndOthers`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredOtherBankLoansReceived`,
          );
        setValue(
          `${prefix}:CurrentUnsecuredBankLoansReceivedAndCurrentPortionOfNoncurrentUnsecuredBankLoansReceived`,
          totalCurrentUnsecuredBankLoans,
        );

        // 3. Total current portion of secured bonds/sukuk/loan stocks (Level 4 total)
        const totalCurrentPortionSecuredBonds =
          getValue(`${customPrefix}:CurrentPortionOfSecuredBonds`) +
          getValue(
            `${customPrefix}:CurrentPortionOfSecuredSukukAsPerShariahCommittee`,
          ) +
          getValue(`${customPrefix}:CurrentPortionOfSecuredMediumTermNotes`) +
          getValue(`${customPrefix}:CurrentPortionOfSecuredLoanStocks`) +
          getValue(`${customPrefix}:CurrentPortionOfSecuredPreferenceShares`);
        setValue(
          `${customPrefix}:CurrentPortionOfSecuredBondsSukukAndLoanStocks`,
          totalCurrentPortionSecuredBonds,
        );

        // 4. Total current portion of unsecured bonds/sukuk/loan stocks (Level 4 total)
        const totalCurrentPortionUnsecuredBonds =
          getValue(`${customPrefix}:CurrentPortionOfUnsecuredBonds`) +
          getValue(
            `${customPrefix}:CurrentPortionOfUnsecuredSukukAsPerShariahCommittee`,
          ) +
          getValue(
            `${customPrefix}:CurrentPortionOfUnsecuredMediumTermNotes`,
          ) +
          getValue(`${customPrefix}:CurrentPortionOfUnsecuredLoanStocks`) +
          getValue(
            `${customPrefix}:CurrentPortionOfUnsecuredPreferenceShares`,
          );
        setValue(
          `${customPrefix}:CurrentPortionOfUnsecuredBondsSukukAndLoanStock`,
          totalCurrentPortionUnsecuredBonds,
        );

        // 5. Total other current borrowings (Level 4 total)
        const totalOtherCurrentBorrowings =
          getValue(`${customPrefix}:OtherCurrentPortionOfTradeLoans`) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfLoanFromSubsidiaries`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfLoanFromAssociates`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfLoanFromJointVentures`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfRedeemablePreferenceShares`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentPortionOfMiscellaneousBorrowings`,
          );
        setValue(
          `${prefix}:OtherCurrentBorrowingsAndCurrentPortionOfOtherNoncurrentBorrowings`,
          totalOtherCurrentBorrowings,
        );

        // 6. Total current borrowings (Level 3 total - sum of all 5 Level 4 totals)
        const totalCurrentBorrowings =
          totalCurrentSecuredBankLoans +
          totalCurrentUnsecuredBankLoans +
          totalCurrentPortionSecuredBonds +
          totalCurrentPortionUnsecuredBonds +
          totalOtherCurrentBorrowings;
        setValue(`${prefix}:ShorttermBorrowings`, totalCurrentBorrowings);
      }

      // #endregion Current Borrowings

      /*
        Current employee benefits - MPERS Calculations
        Current employee benefits liabilities - MFRS Calculations
      */
      // #region Current employee benefits

      // Total current employee benefit liabilities (Level 3 total)
      // MPERS: 6 fields (no Retirement Benefits) vs MFRS: 7 fields
      const totalCurrentEmployeeBenefitLiabilities =
        prefix === 'ifrs-smes'
          ? getValue(
              `${customPrefix}:CurrentCashSettledShareBasedPaymentLiability`,
            ) +
            getValue(`${customPrefix}:CurrentEmploymentTerminationBenefits`) +
            getValue(`${customPrefix}:CurrentProvisionForUnconsumedLeave`) +
            getValue(`${customPrefix}:CurrentDefinedContributionPlan`) +
            getValue(`${customPrefix}:CurrentDefinedBenefitPlan`) +
            getValue(`${customPrefix}:CurrentOtherEmployeeBenefitLiabilities`)
          : getValue(
              `${customPrefix}:CurrentCashSettledShareBasedPaymentLiability`,
            ) +
            getValue(`${customPrefix}:CurrentRetirementBenefits`) +
            getValue(`${customPrefix}:CurrentEmploymentTerminationBenefits`) +
            getValue(`${customPrefix}:CurrentProvisionForUnconsumedLeave`) +
            getValue(`${customPrefix}:CurrentDefinedContributionPlan`) +
            getValue(`${customPrefix}:CurrentDefinedBenefitPlan`) +
            getValue(
              `${customPrefix}:CurrentOtherEmployeeBenefitLiabilities`,
            );
      setValue(
        `${prefix}:CurrentProvisionsForEmployeeBenefits`,
        totalCurrentEmployeeBenefitLiabilities,
      );

      // #endregion Current employee benefits

      /*
        Current provision - MPERS and MFRS Calculations
      */
      // #region Current provisions

      // 1. Total current provisions (Level 3 total)
      // MPERS: 6 fields (no Refunds provision) vs MFRS: 7 fields
      const totalCurrentProvisions =
        prefix === 'ifrs-smes'
          ? getValue(`${prefix}:ShorttermWarrantyProvision`) +
            getValue(`${prefix}:ShorttermRestructuringProvision`) +
            getValue(`${prefix}:ShorttermLegalProceedingsProvision`) +
            getValue(`${prefix}:ShorttermOnerousContractsProvision`) +
            getValue(
              `${customPrefix}:ShorttermProvisionForDecommissioningRestorationAndRehabilitationCosts`,
            ) +
            getValue(`${prefix}:OtherShorttermProvisions`)
          : getValue(`${prefix}:ShorttermWarrantyProvision`) +
            getValue(`${prefix}:ShorttermRestructuringProvision`) +
            getValue(`${prefix}:ShorttermLegalProceedingsProvision`) +
            getValue(`${prefix}:CurrentRefundsProvision`) +
            getValue(`${prefix}:ShorttermOnerousContractsProvision`) +
            getValue(
              `${prefix}:ShorttermProvisionForDecommissioningRestorationAndRehabilitationCosts`,
            ) +
            getValue(`${prefix}:OtherShorttermProvisions`);
      setValue(`${prefix}:CurrentProvisions`, totalCurrentProvisions);

      // #endregion Current provisions

      /*
        Trade and Other Current Payables - MPERS and MFRS Calculations
      */
      // #region Trade and Other Current Payables

      // 1. Total current trade payables (Level 4 total)
      // MPERS: 7 fields (no generic Trade payables) vs MFRS: 8 fields
      const totalCurrentTradePayables =
        prefix === 'ifrs-smes'
          ? getValue(
              `${customPrefix}:CurrentTradePayableDueToContractCustomer`,
            ) +
            getValue(
              `${customPrefix}:CurrentTradePayablesDueToHoldingCompany`,
            ) +
            getValue(
              `${customPrefix}:CurrentTradePayablesDueToSubsidiaries`,
            ) +
            getValue(`${customPrefix}:CurrentTradePayablesDueToAssociates`) +
            getValue(
              `${customPrefix}:CurrentTradePayablesDueToJointVentures`,
            ) +
            getValue(
              `${customPrefix}:CurrentTradePayablesDueToRelatedCompanies`,
            ) +
            getValue(`${customPrefix}:OtherCurrentTradePayables`)
          : getValue(`${customPrefix}:CurrentTradePayablesDueToCustomers`) +
            getValue(
              `${customPrefix}:CurrentTradePayableDueToContractCustomer`,
            ) +
            getValue(
              `${customPrefix}:CurrentTradePayablesDueToHoldingCompany`,
            ) +
            getValue(
              `${customPrefix}:CurrentTradePayablesDueToSubsidiaries`,
            ) +
            getValue(`${customPrefix}:CurrentTradePayablesDueToAssociates`) +
            getValue(
              `${customPrefix}:CurrentTradePayablesDueToJointVentures`,
            ) +
            getValue(
              `${customPrefix}:CurrentTradePayablesDueToRelatedCompanies`,
            ) +
            getValue(`${customPrefix}:OtherCurrentTradePayables`);
      setValue(
        `${prefix}:TradeAndOtherCurrentPayablesToTradeSuppliers`,
        totalCurrentTradePayables,
      );

      // 2. Total other current payables due to related parties (Level 5 total)
      const totalOtherCurrentPayablesDueToRelatedParties =
        getValue(`${customPrefix}:OtherCurrentPayablesDueToHoldingCompany`) +
        getValue(`${customPrefix}:OtherCurrentPayablesDueToSubsidiaries`) +
        getValue(`${customPrefix}:OtherCurrentPayablesDueToAssociates`) +
        getValue(`${customPrefix}:OtherCurrentPayablesDueToJointVentures`) +
        getValue(
          `${customPrefix}:OtherCurrentPayablesDueToOtherRelatedParties`,
        );
      setValue(
        `${customPrefix}:OtherCurrentPayablesDueToRelatedParties`,
        totalOtherCurrentPayablesDueToRelatedParties,
      );

      // 3. Total other payables due to non-controlling interests (Level 5 total)
      // MFRS only - MPERS does not have this section
      let totalOtherPayablesDueToNonControllingInterests = 0;
      if (prefix === 'ifrs-full') {
        totalOtherPayablesDueToNonControllingInterests =
          getValue(
            `${customPrefix}:OtherCurrentDividendPayableToNoncontrollingInterest`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentLoansFromNoncontrollingInterest`,
          ) +
          getValue(
            `${customPrefix}:OtherCurrentMiscellaneousPayableDueToNoncontrollingInterest`,
          );
        setValue(
          `${customPrefix}:OtherCurrentPayablesDueToNoncontrollingInterests`,
          totalOtherPayablesDueToNonControllingInterests,
        );
      }

      // 4. Total current non-trade payables (Level 5 total)
      // MPERS: 4 fields vs MFRS: 8 fields
      const totalCurrentNontradePayables =
        prefix === 'ifrs-smes'
          ? getValue(`${customPrefix}:CurrentNontradeDeferredIncome`) +
            getValue(`${customPrefix}:CurrentNontradeAccruals`) +
            getValue(`${customPrefix}:CurrentNontradeRetentionPayable`) +
            getValue(`${customPrefix}:OtherCurrentNontradePayables`)
          : getValue(`${customPrefix}:CurrentNontradeAccruals`) +
            getValue(`${customPrefix}:CurrentNontradeRetentionPayable`) +
            getValue(`${customPrefix}:CurrentNontradeDeferredIncome`) +
            getValue(
              `${customPrefix}:CurrentNontradeDepositsAndAdvancedBillings`,
            ) +
            getValue(`${customPrefix}:CurrentNontradeFinancingCosts`) +
            getValue(`${customPrefix}:CurrentNontradeDividendPayable`) +
            getValue(`${customPrefix}:CurrentNontradeInterestPayable`) +
            getValue(`${customPrefix}:OtherCurrentNontradePayables`);
      setValue(
        `${customPrefix}:CurrentNontradePayables`,
        totalCurrentNontradePayables,
      );

      // 5. Total other current payables (Level 4 total)
      // MPERS: sum of 2 Level 5 totals, MFRS: sum of 3 Level 5 totals
      const totalOtherCurrentPayables =
        prefix === 'ifrs-smes'
          ? totalOtherCurrentPayablesDueToRelatedParties +
            totalCurrentNontradePayables
          : totalOtherCurrentPayablesDueToRelatedParties +
            totalOtherPayablesDueToNonControllingInterests +
            totalCurrentNontradePayables;
      setValue(
        prefix === 'ifrs-smes'
          ? `${customPrefix}:OtherCurrentPayables`
          : `${prefix}:OtherCurrentPayables`,
        totalOtherCurrentPayables,
      );

      // 6. Total trade and other current payables (Level 3 total - sum of 2 Level 4 totals)
      const totalTradeAndOtherCurrentPayables =
        totalCurrentTradePayables + totalOtherCurrentPayables;
      setValue(
        `${prefix}:TradeAndOtherCurrentPayables`,
        totalTradeAndOtherCurrentPayables,
      );

      // #endregion Trade and Other Current Payables

      /*
        Current Derivative Financial Liabilities - MFRS Calculations
      */
      // #region Current Derivative Financial Liabilities
      // MFRS only - MPERS does not have this section
      if (prefix === 'ifrs-full') {
        // 1. Total current derivatives at fair value through profit or loss (Level 4 total)
        const totalCurrentDerivativeLiabilitiesFVTPL =
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesForwardContract`,
          ) +
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesOptions`,
          ) +
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesSwap`,
          ) +
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesOthers`,
          );
        setValue(
          `${customPrefix}:CurrentDerivativeFinancialLiabilitiesAtFairValueThroughProfitOrLoss`,
          totalCurrentDerivativeLiabilitiesFVTPL,
        );

        // 2. Total current derivatives used for hedging (Level 4 total)
        const totalCurrentDerivativeLiabilitiesHedging =
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesForwardContractUsedForHedging`,
          ) +
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesOptionsUsedForHedging`,
          ) +
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesSwapUsedForHedging`,
          ) +
          getValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesOthersUsedForHedging`,
          );
        setValue(
          `${customPrefix}:CurrentDerivativeFinancialLiabilitiesUsedForHedging`,
          totalCurrentDerivativeLiabilitiesHedging,
        );

        // 3. Total current derivative financial liabilities (Level 3 total)
        const totalCurrentDerivativeFinancialLiabilities =
          totalCurrentDerivativeLiabilitiesFVTPL +
          totalCurrentDerivativeLiabilitiesHedging +
          getValue(
            `${customPrefix}:OtherCurrentDerivativeFinancialLiabilities`,
          );
        setValue(
          `${prefix}:CurrentDerivativeFinancialLiabilities`,
          totalCurrentDerivativeFinancialLiabilities,
        );
      }

      // #endregion Current Derivative Financial Liabilities
    });
  });

  return calculatedTotals;
}
